import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Datafeed,
  DatafeedEmail,
  DatafeedLogEntry,
  DatafeedOperation,
  LifeRaftQuery,
  LifeRaftSubject,
  LifeRaftTracking,
  CreatePipelineRequest,
  OpenEyeResponse,
  DataminrWatchlist,
  SftpConnectionInformation,
  SftpConnectionTestResults,
  LiveViewTokenResponse,
  DatafeedUploadedFile
} from './datafeed.model';
import { QueryParams, QueryResult } from '../../../core/store/models';
import { HttpService } from '../../../core/services/http.service';
import { UploadedFile } from 'src/app/modules/core/components/file-upload/uploadedFile.model';
import { PipelineDefinition } from '.';

import { API_BASE_URL as apiBaseUrl } from '@app/core/constants/api-base-url';

@Injectable({
  providedIn: 'root'
})
export class DatafeedApiService {
  constructor(private http: HttpService) {}

  public getDatafeeds(params: QueryParams): Observable<QueryResult<Datafeed>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/pipelines?${queryString}`;

    return this.http.get<QueryResult<Datafeed>>(url);
  }

  public getDatafeed(datafeedUId: string): Observable<Datafeed> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}?includeOperations=true`;

    return this.http.get<Datafeed>(url);
  }

  public runDatafeedImmediately(datafeedUId: string, settings?: any): Observable<Datafeed> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/execute`;

    const request = {
      settings: settings
    };
    return this.http.post<Datafeed>(url, request);
  }

  public getDatafeedEmails(datafeedUId: string): Observable<DatafeedEmail> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/email`;

    return this.http.get<DatafeedEmail>(url);
  }

  public getDatafeedFiles(datafeedUId: string): Observable<DatafeedUploadedFile[]> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/uploaded-files`;

    return this.http.get<DatafeedUploadedFile[]>(url);
  }

  public downloadDatafeedFile(datafeedUId: string, fileName: string): void {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/stream-uploaded-file/${encodeURIComponent(fileName)}`;
    window.open(url, '_blank');
  }

  public insertDatafeed(datafeed: Datafeed): Observable<Datafeed> {
    const url = `${apiBaseUrl}/v1/pipelines`;

    return this.http.post<Datafeed>(url, datafeed);
  }

  public updateDatafeed(datafeed: Datafeed): Observable<Datafeed> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeed.uId}`;

    return this.http.put<Datafeed>(url, datafeed);
  }

  public deleteDatafeed(datafeed: Datafeed): Observable<void> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeed.uId}`;

    return this.http.delete(url);
  }

  public getDatafeedLogs(datafeedUId: string, params: QueryParams): Observable<QueryResult<DatafeedLogEntry>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/logs?${queryString}`;

    return this.http.get<QueryResult<DatafeedLogEntry>>(url);
  }

  public getDatafeedOperations(datafeed: Datafeed): Observable<QueryResult<DatafeedOperation>> {
    const queryString = this.http.queryStringFromObject({ skip: 0, take: 1000 });

    const url = `${apiBaseUrl}/v1/pipelines/${datafeed.uId}/operations?${queryString}`;

    return this.http.get<QueryResult<DatafeedLogEntry>>(url);
  }

  public getDatafeedOperation(datafeedUId: string, operationUId: string): Observable<DatafeedOperation> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/operations/${operationUId}`;

    return this.http.get<DatafeedLogEntry>(url);
  }


  public updateDatafeedOperation(datafeed: Datafeed, operation: DatafeedOperation): Observable<DatafeedOperation> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeed.uId}/operations/${operation.uId}`;

    return this.http.put<DatafeedOperation>(url, operation);
  }

  public deleteDatafeedOperation(datafeed: Datafeed, operation: DatafeedOperation): Observable<void> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeed.uId}/operations/${operation.uId}`;

    return this.http.delete(url);
  }

  public uploadDatafeedFile(datafeedUId: string, uploadedFile: UploadedFile): Observable<void> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/execute/with-files`;

    const data = new FormData();
    data.append('file', uploadedFile.file);

    return this.http.post(url, data);
  }

  public getLifeRaftLiveQueries(datafeedUId: string): Observable<LifeRaftQuery[]> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/liferaft/live`;

    return this.http.get<LifeRaftQuery[]>(url);
  }

  public getLifeRaftFusionQueries(datafeedUId: string): Observable<LifeRaftQuery[]> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/liferaft/fusion`;

    return this.http.get<LifeRaftQuery[]>(url);
  }

  public getLifeRaftDarkWebTrackings(datafeedUId: string): Observable<LifeRaftTracking[]> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/liferaft/darkWeb`;

    return this.http.get<LifeRaftTracking[]>(url);
  }

  public getLifeRaftFollowingsSubjects(datafeedUId: string): Observable<LifeRaftSubject[]> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/liferaft/followings`;

    return this.http.get<LifeRaftSubject[]>(url);
  }

  public getDataminrWatchlists(datafeedUId: string): Observable<DataminrWatchlist[]> {
    const url = `${apiBaseUrl}/v1/pipelines/${datafeedUId}/dataminr/watchlists`;

    return this.http.get<DataminrWatchlist[]>(url);
  }

  public getPipelineDefinitions(params: QueryParams): Observable<QueryResult<PipelineDefinition>> {
    const queryString = this.http.queryStringFromObject(params);

    const url = `${apiBaseUrl}/v1/pipelines/pipeline-definitions?${queryString}`;

    return this.http.get<QueryResult<PipelineDefinition>>(url);
  }

  public createPipelineDefinition(request: CreatePipelineRequest): Observable<Datafeed> {
    const url = `${apiBaseUrl}/v1/pipelines/pipeline-definitions`;

    return this.http.post<Datafeed>(url);
  }

  public getOpenEyeCameraStreamUrl(
    companyId: string,
    deviceId: string,
    cameraId: string,
    streamName: string,
    pipelineDefinitionUId: string
  ): Observable<OpenEyeResponse> {
    const url = `${apiBaseUrl}/v1/pipelines/openeye/stream?companyId=${companyId}&deviceId=${deviceId}&cameraId=${cameraId}&streamName=${streamName}&pipelineDefinitionUId=${pipelineDefinitionUId}`;
    return this.http.get<string>(url);
  }

  public getOpenEyeCameraImageUrl(
    companyId: string,
    deviceId: string,
    cameraId: string,
    streamName: string,
    pipelineDefinitionUId: string
  ): Observable<OpenEyeResponse> {
    const url = `${apiBaseUrl}/v1/pipelines/openeye/image?companyId=${companyId}&deviceId=${deviceId}&cameraId=${cameraId}&streamName=${streamName}&pipelineDefinitionUId=${pipelineDefinitionUId}`;
    return this.http.get<string>(url);
  }

  public testSftpConnectionInfoForOperation(
    pipelineUId: string,
    operationUId: string,
    sftpConnectionInformation: SftpConnectionInformation
  ): Observable<SftpConnectionTestResults> {
    const url = `${apiBaseUrl}/v1/pipelines/${pipelineUId}/operations/${operationUId}/test-sftp-connection`;
    return this.http.post<SftpConnectionTestResults>(url, sftpConnectionInformation);
  }

  public getLiveViewToken(pipelineDefinitionUId: string): Observable<LiveViewTokenResponse> {
    const url = `${apiBaseUrl}/v1/pipelines/live-view/token?pipelineDefinitionUId=${pipelineDefinitionUId}`;

    return this.http.get(url);
  }
}
